import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Plus, Check, Sun, Sunrise, Moon, ChevronRight, Lightbulb, Trash2, Pencil } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

const QUOTES = [
  "The secret of getting ahead is getting started.",
  "Small steps every day lead to big results.",
  "Focus on being productive instead of busy.",
  "You don't have to be great to start, but you have to start to be great.",
  "What you do today can improve all your tomorrows.",
  "A goal without a plan is just a wish.",
  "Don't watch the clock; do what it does — keep going.",
  "Start where you are. Use what you have. Do what you can.",
  "Action is the foundational key to all success.",
  "Productivity is never an accident.",
];

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return { text: "नमस्कार", Icon: Sunrise };
  if (hour < 17) return { text: "नमस्कार", Icon: Sun };
  return { text: "नमस्कार", Icon: Moon };
}

function getTodayStr() {
  return new Date().toISOString().split("T")[0];
}

function getFormattedDate() {
  const now = new Date();
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return {
    dayName: days[now.getDay()],
    dayNum: now.getDate(),
    month: months[now.getMonth()],
    year: now.getFullYear(),
  };
}

export default function MyDayView({ user, tasks, onTasksChange }) {
  const [newTask, setNewTask] = useState("");
  const [quote] = useState(() => QUOTES[Math.floor(Math.random() * QUOTES.length)]);
  const [suggestions, setSuggestions] = useState([]);

  const greeting = getGreeting();
  const today = getTodayStr();
  const dateInfo = getFormattedDate();

  // Auto-carryover: bump uncompleted tasks from past days into today's My Day
  useEffect(() => {
    const carryover = tasks.filter(t =>
      !t.completed && t.my_day_date && t.my_day_date < today
    );
    if (carryover.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        await Promise.all(
          carryover.map(t =>
            fetch(`${API}/tasks/${t.task_id}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ my_day_date: today }),
            })
          )
        );
        if (!cancelled) onTasksChange();
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today]);

  // Today's tasks: my_day_date is today OR due_date is today
  const todayTasks = tasks.filter(t =>
    t.my_day_date === today || (t.due_date && t.due_date.split("T")[0] === today)
  );
  const completedToday = todayTasks.filter(t => t.completed);
  const pendingToday = todayTasks.filter(t => !t.completed);

  // Suggestions: tasks without my_day_date, overdue, or from past days
  useEffect(() => {
    const overdue = tasks.filter(t => {
      if (t.completed) return false;
      if (t.my_day_date === today) return false;
      if (t.due_date && t.due_date.split("T")[0] === today) return false;
      // Past due (no my_day_date assigned yet)
      if (!t.my_day_date && t.due_date && t.due_date.split("T")[0] < today) return true;
      // General tasks without my_day_date
      if (!t.my_day_date && !t.completed) return true;
      return false;
    });
    setSuggestions(overdue.slice(0, 10));
  }, [tasks, today]);

  const handleAddTask = async () => {
    if (!newTask.trim()) return;
    try {
      const res = await fetch(`${API}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: newTask.trim(),
          my_day_date: today,
          due_date: new Date().toISOString(),
          status: "urgent-important",
        }),
      });
      if (res.ok) {
        setNewTask("");
        onTasksChange();
        toast.success("Task added to My Day");
      }
    } catch {
      toast.error("Failed to add task");
    }
  };

  const handleToggleTask = async (task) => {
    try {
      await fetch(`${API}/tasks/${task.task_id}/toggle`, {
        method: "PATCH",
        credentials: "include",
      });
      onTasksChange();
    } catch {
      toast.error("Failed to update task");
    }
  };

  const handleAddToMyDay = async (task) => {
    try {
      await fetch(`${API}/tasks/${task.task_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ my_day_date: today }),
      });
      onTasksChange();
      toast.success("Added to My Day");
    } catch {
      toast.error("Failed to add to My Day");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await fetch(`${API}/tasks/${taskId}`, {
        method: "DELETE",
        credentials: "include",
      });
      onTasksChange();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleEditTask = async (taskId, newTitle) => {
    const title = (newTitle || "").trim();
    if (!title) {
      toast.error("Title cannot be empty");
      return false;
    }
    try {
      const res = await fetch(`${API}/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title }),
      });
      if (res.ok) {
        onTasksChange();
        toast.success("Task updated");
        return true;
      }
      toast.error("Failed to update");
      return false;
    } catch {
      toast.error("Failed to update");
      return false;
    }
  };

  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Main My Day Area */}
      <div className="flex-1 flex flex-col">
        {/* Hero Section */}
        <div className="px-8 pt-8 pb-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <greeting.Icon className="w-7 h-7 text-amber-400" />
                <h1 className="text-2xl font-bold text-white">{greeting.text}, {firstName}</h1>
              </div>
              <p className="text-sm text-gray-400 italic ml-10">"{quote}"</p>
            </div>

            {/* Date Card */}
            <div className="flex-shrink-0 bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-center min-w-[120px]">
              <p className="text-xs text-gray-400 uppercase tracking-wider">{dateInfo.dayName}</p>
              <p className="text-3xl font-bold text-white my-1">{dateInfo.dayNum}</p>
              <p className="text-xs text-gray-400">{dateInfo.month} {dateInfo.year}</p>
            </div>
          </div>
        </div>

        {/* Task Input */}
        <div className="px-8 pb-4">
          <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
            <Plus className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <input
              data-testid="my-day-task-input"
              type="text"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              placeholder="Add a task for today..."
              className="flex-1 bg-transparent text-white placeholder:text-gray-500 outline-none text-sm"
              onKeyDown={(e) => { if (e.key === "Enter") handleAddTask(); }}
            />
            {newTask.trim() && (
              <button
                onClick={handleAddTask}
                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-lg transition-colors"
                data-testid="my-day-add-btn"
              >
                Add
              </button>
            )}
          </div>
        </div>

        {/* Today's Tasks */}
        <ScrollArea className="flex-1 px-8">
          {pendingToday.length === 0 && completedToday.length === 0 && (
            <div className="text-center py-16">
              <Sun className="w-16 h-16 text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-400 mb-1">Focus on your day</h3>
              <p className="text-sm text-gray-600">Get started by adding tasks for today</p>
            </div>
          )}

          {/* Pending Tasks */}
          {pendingToday.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-medium">
                To Do — {pendingToday.length} task{pendingToday.length !== 1 ? "s" : ""}
              </h3>
              <div className="space-y-1">
                {pendingToday.map(task => (
                  <TaskRow key={task.task_id} task={task} onToggle={handleToggleTask} onDelete={handleDeleteTask} onEdit={handleEditTask} />
                ))}
              </div>
            </div>
          )}

          {/* Completed Tasks */}
          {completedToday.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-medium">
                Completed — {completedToday.length}
              </h3>
              <div className="space-y-1">
                {completedToday.map(task => (
                  <TaskRow key={task.task_id} task={task} onToggle={handleToggleTask} onDelete={handleDeleteTask} onEdit={handleEditTask} />
                ))}
              </div>
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Suggestions Panel */}
      <div className="w-72 border-l border-white/10 flex flex-col flex-shrink-0" style={{ backgroundColor: '#1e2126' }}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-white">Suggestions</h3>
          </div>
          <p className="text-xs text-gray-500 mt-1">Tasks you might want to add to today</p>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-1">
            {suggestions.length === 0 && (
              <p className="text-xs text-gray-600 text-center py-8">No suggestions right now</p>
            )}
            {suggestions.map(task => (
              <div
                key={task.task_id}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 group transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-300 truncate">{task.title || "Untitled"}</p>
                  {task.due_date && (
                    <p className="text-[10px] text-red-400">
                      Due: {new Date(task.due_date).toLocaleDateString([], { month: "short", day: "numeric" })}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleAddToMyDay(task)}
                  className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-all"
                  title="Add to My Day"
                  data-testid={`add-to-myday-${task.task_id}`}
                >
                  <Plus className="w-3.5 h-3.5 text-blue-400" />
                </button>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

function TaskRow({ task, onToggle, onDelete, onEdit }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(task.title || "");

  const startEdit = () => {
    setDraft(task.title || "");
    setIsEditing(true);
  };

  const saveEdit = async () => {
    if (draft.trim() === (task.title || "").trim()) {
      setIsEditing(false);
      return;
    }
    const ok = await onEdit(task.task_id, draft);
    if (ok !== false) setIsEditing(false);
  };

  const cancelEdit = () => {
    setDraft(task.title || "");
    setIsEditing(false);
  };

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 group transition-colors" data-testid={`myday-task-${task.task_id}`}>
      <button
        onClick={() => onToggle(task)}
        className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
          task.completed
            ? "border-green-500 bg-green-500"
            : "border-gray-500 hover:border-blue-400"
        }`}
        data-testid={`myday-toggle-${task.task_id}`}
      >
        {task.completed && <Check className="w-3 h-3 text-white" />}
      </button>
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveEdit();
              else if (e.key === "Escape") cancelEdit();
            }}
            className="w-full bg-white/5 border border-blue-500/60 rounded px-2 py-1 text-sm text-white outline-none"
            data-testid={`myday-edit-input-${task.task_id}`}
          />
        ) : (
          <p className={`text-sm ${task.completed ? "text-gray-500 line-through" : "text-white"}`}>
            {task.title || "Untitled"}
          </p>
        )}
        {!isEditing && task.due_time && (
          <p className="text-[10px] text-gray-500">{task.due_time}</p>
        )}
        {!isEditing && task.added_by_email && task.added_by_name && (
          <span
            className="inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30"
            title={`Added by ${task.added_by_name} (${task.added_by_email})`}
            data-testid={`myday-task-added-by-${task.task_id}`}
          >
            Added by {task.added_by_name}
          </span>
        )}
      </div>
      {!isEditing && task.tags?.length > 0 && (
        <div className="flex gap-1">
          {task.tags.slice(0, 2).map((tag, i) => (
            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">{tag}</span>
          ))}
        </div>
      )}
      {!isEditing && (
        <div className="flex items-center gap-1">
          <button
            onClick={startEdit}
            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity"
            title="Edit task"
            data-testid={`myday-edit-btn-${task.task_id}`}
          >
            <Pencil className="w-3.5 h-3.5 text-blue-400" />
          </button>
          <button
            onClick={() => onDelete(task.task_id)}
            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity"
            title="Delete task"
            data-testid={`myday-delete-btn-${task.task_id}`}
          >
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </button>
        </div>
      )}
    </div>
  );
}
